import {
  formSelector,
  getState,
  onlyIfForm,
  removeForm,
  setForm,
  updateForm,
  useSelector,
} from '../../../store.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {
  dimensionHasError,
  dimensionsSelector,
  labelTypeSelector,
  maxWeightSelector,
  orderNumberFromLabelConfigSelector,
  orderTypeSelector,
  referenceIDFromLabelConfigSelector,
  updateParcel,
} from '../../../data/labelInfos/index.js'
import {getBulkRates} from '../BulkLabelConfigForm/bulkLabelConfigActions.js'
import {getRates} from '../../../data/labelInfos/rateRequest.js'
import WeightInput from '../../../common/components/Form/WeightInput.js'
import {LABEL_TYPE_SHIPPING} from '../../../common/constants/LabelConfig.js'
import {
  OT_RETURN_ORDER,
  OT_V3_ORDER,
} from '../../../common/constants/OrderTypes.js'
import {
  Count,
  IfPlural,
  IfSingle,
  Plural,
  PluralBlock,
} from '../../../common/components/Plural.js'
import DimensionsInput from '../../../common/components/Form/DimensionsInput.js'
import delay from '../../../common/delay.js'

export const MODAL_FORM = 'edit_weight_dimensions_modal'

export async function showEditWeightDimensionsModal({
  labelInfoIDs,
  packagesIndex = 0,
  focusOn = 'weight',
}) {
  const weight = maxWeightSelector(getState(), {labelInfoIDs, packagesIndex})
  const dimensions = dimensionsSelector(getState(), {
    labelInfoID: labelInfoIDs[0],
    packagesIndex,
  })
  const orderType = orderTypeSelector(getState(), {
    labelInfoID: labelInfoIDs[0],
  })

  // count up shipping labels to know if this is mixed with shipping and return labels
  const shippingCount = labelInfoIDs.reduce(
    (count, labelInfoID) =>
      count +
      (labelTypeSelector(getState(), {labelInfoID}) === LABEL_TYPE_SHIPPING
        ? 1
        : 0),
    0,
  )

  // item count is
  // shipping label count if we more labelInfoIDs than shipping labels
  // otherwise labelInfoID count
  const itemCount =
    shippingCount && shippingCount !== labelInfoIDs.length
      ? shippingCount
      : labelInfoIDs.length

  const orderNumber =
    orderType === OT_V3_ORDER && itemCount === 1
      ? orderNumberFromLabelConfigSelector(getState(), {
          labelInfoID: labelInfoIDs[0],
        })
      : null
  const referenceID =
    orderType === OT_RETURN_ORDER && itemCount === 1
      ? referenceIDFromLabelConfigSelector(getState(), {
          labelInfoID: labelInfoIDs[0],
        })
      : null

  setForm(MODAL_FORM, {
    labelInfoIDs,
    orderType,
    itemCount,
    orderNumber,
    referenceID,
    packagesIndex,
    focusOn,
    weight,
    length: dimensions.length,
    width: dimensions.width,
    height: dimensions.height,
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(updates) {
  updateForm(MODAL_FORM, updates)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formSelector(state, {formName: MODAL_FORM})
}

export function errorsSelector(state) {
  const {lbError, ozError, length, width, height} = modalFormSelector(state)

  const errors = {}

  if (lbError || ozError) {
    errors.preventSave = true
  }

  if (dimensionHasError(length)) {
    errors.length = 'Invalid dimension'
    errors.preventSave = true
  }

  if (dimensionHasError(width)) {
    errors.width = 'Invalid dimension'
    errors.preventSave = true
  }

  if (dimensionHasError(height)) {
    errors.height = 'Invalid dimension'
    errors.preventSave = true
  }

  return errors
}

export async function saveWeightDimensions() {
  try {
    updateModalForm({isSaving: true, serverError: null})

    // wait for inputs to finish debouncing
    await delay(500)

    let {labelInfoIDs, packagesIndex, weight, length, width, height} =
      modalFormSelector(getState())

    length = Number(length)
    width = Number(width)
    height = Number(height)

    labelInfoIDs.map((labelInfoID) =>
      updateParcel(labelInfoID, packagesIndex, {weight, length, width, height}),
    )

    showMessageToast('Weight and Dimensions have been updated')

    closeModal()

    await getBulkRates(labelInfoIDs)

    await getRates(labelInfoIDs)
  } catch (error) {
    updateModalForm({
      serverError: error.message || error.error_message,
      isSaving: false,
    })
  }
}

function EditWeightDimensionsModal({form}) {
  const errors = useSelector(errorsSelector)

  return (
    <ConfirmModal
      title="Edit Weight and Dimensions"
      confirmText="Save"
      cancelText="Cancel"
      modalSize="sm"
      isSaving={form.isSaving}
      isDisabled={errors.preventSave}
      onConfirm={() => saveWeightDimensions()}
      onCancel={() => closeModal()}
      error={form.serverError}
    >
      <ul className="list">
        <PluralBlock count={form.itemCount}>
          <li className="list__item--form list__item--no-style fs-00 text--md-grey margin-bottom-25">
            <strong>
              Editing{' '}
              <IfSingle>{form.orderNumber || form.referenceID}</IfSingle>
              <IfPlural>
                <Count />{' '}
                <Plural
                  word={form.orderType === OT_V3_ORDER ? 'Order' : 'RMA'}
                />
              </IfPlural>
            </strong>
          </li>
        </PluralBlock>
        <li className="list__item--form list__item--no-style">
          <div className="wrap--md-number-input">
            <label className="label--bold" htmlFor="id_weight_lb">
              Weight
            </label>

            <WeightInput
              idLb="id_weight-lb"
              idOz="id_weight-oz"
              weightOz={form.weight}
              setWeightOz={(weight) => updateModalForm({weight})}
              onErrorChange={({lb, oz}) =>
                updateModalForm({lbError: lb, ozError: oz})
              }
              showScaleControl
              autoFocus={form.focusOn === 'weight'}
              widthPerCharacter={0.95}
            />
          </div>
        </li>
        <li className="list__item--form list__item--no-style">
          <div className="wrap--md-number-input">
            <label className="label--bold margin-bottom-3">Dimensions</label>
            <DimensionsInput
              length={form.length}
              width={form.width}
              height={form.height}
              setLength={(length) => updateModalForm({length})}
              setWidth={(width) => updateModalForm({width})}
              setHeight={(height) => updateModalForm({height})}
              errorMessage={errors.height || errors.length || errors.width}
              hasLengthError={!!errors.length}
              hasWidthError={!!errors.width}
              hasHeightError={!!errors.height}
              autoFocus={form.focusOn === 'dimensions'}
              widthPerCharacter={0.95}
            />
          </div>
        </li>
      </ul>
    </ConfirmModal>
  )
}

export default onlyIfForm(EditWeightDimensionsModal, modalFormSelector)
